import type {PropsOf} from '@chakra-ui/react';
import {Box, chakra} from '@chakra-ui/react';
import type {MotionProps} from 'motion/react';
import {AnimatePresence, motion} from 'motion/react';
import {forwardRef} from 'react';
import type {TooltipProps} from 'recharts';

import {useChartTooltipStyleConfig} from '../../theme-config';

export type Props = PropsOf<typeof Box>;

const MotionDiv = chakra(motion.div);

const ChartTooltipInternal = forwardRef(
  (
    {children, motionProps, ...props}: Props & {motionProps?: MotionProps} & TooltipProps<any, any> = {motionProps: {}},
    ref,
  ) => {
    const style = useChartTooltipStyleConfig(`container`);
    return (
      <AnimatePresence>
        {/* @ts-expect-error: Chakra 'transition' collapse with framer-motion 'transition' */}
        <MotionDiv initial={`exit`} animate={`enter`} exit={`exit`} {...motionProps}>
          {/* @ts-expect-error: Chakra and recharts has collapsing style attributes */}
          <Box sx={style} {...props} ref={ref}>
            {children}
          </Box>
        </MotionDiv>
      </AnimatePresence>
    );
  },
);

const Title = ({children, ...props}: Props) => {
  const style = useChartTooltipStyleConfig(`title`);
  return (
    <Box as={`h3`} sx={style} {...props}>
      {children}
    </Box>
  );
};

const Header = ({children, colorScheme, ...props}: Props & {colorScheme?: string}) => {
  const style = useChartTooltipStyleConfig(`header`, {colorScheme});
  return (
    <Box sx={style} colorScheme={colorScheme} {...props}>
      {children}
    </Box>
  );
};

const Body = ({children, ...props}: Props) => {
  const style = useChartTooltipStyleConfig(`body`);
  return (
    <Box sx={style} {...props}>
      {children}
    </Box>
  );
};

const Item = ({children, ...props}: Props) => {
  const style = useChartTooltipStyleConfig(`item`);
  return (
    <Box sx={style} {...props}>
      {children}
    </Box>
  );
};

type ChartTooltipType = typeof ChartTooltipInternal & {
  Title: typeof Title;
  Header: typeof Header;
  Body: typeof Body;
  Item: typeof Item;
};

const ChartTooltip = ChartTooltipInternal as ChartTooltipType;

ChartTooltip.displayName = `ChartTooltip`;

ChartTooltip.Title = Title;
ChartTooltip.Header = Header;
ChartTooltip.Body = Body;
ChartTooltip.Item = Item;

export default ChartTooltip;
export type {Props as ChartTooltipProps};
